import type { EpilotControlProps } from '@epilot/journey-logic-commons'
import type { ComponentType } from 'react'
import React, { Suspense } from 'react'

export type FallbackProps = Pick<EpilotControlProps, 'uischema'>

/**
 * Utility helper used to lazy-load a control component for a specified amount of time,
 * or "forever" if Infinity is provided as the timeout.
 *
 * WARNING: This function is only meant to be used during Development.
 * Please use {@link React.lazy} directly for Production.
 */
export const lazyControl = <T extends ComponentType<unknown>>(
  factory: Parameters<typeof React.lazy<T>>[0],
  timeout: number = Infinity
) => {
  return React.lazy(
    () =>
      new Promise((resolve) =>
        setTimeout(
          () => {
            factory().then(resolve as never)
          },
          timeout === Infinity ? 2147483647 : timeout
        )
      )
  )
}

/**
 * Renders a Suspense-enabled version of Control, with Fallback being shown
 * while the actual control component is lazy-loaded.
 *
 * Useful to wrap JSON Forms control components and offer skeleton alternatives while loading.
 */
export const SuspendedControl = (
  /** Lazy loaded version of the control component */
  Control: ReturnType<typeof React.lazy>,
  /** Fallback component */
  Fallback: NonNullable<React.ReactNode>
) => {
  const suspended = (controlProps: EpilotControlProps) => (
    <Suspense
      fallback={
        typeof Fallback === 'function' ? (
          <Fallback {...controlProps} uischema={controlProps.uischema} />
        ) : (
          Fallback
        )
      }
    >
      <Control {...controlProps} />
    </Suspense>
  )

  suspended.displayName = 'SuspendedControl'

  return suspended
}
